import { useCallback, useEffect, useState } from "react";
import { CommandCenterVisual } from "./components/CommandCenterVisual";
import { LoginPanel } from "./components/LoginPanel";
import { SystemHeader } from "./components/SystemHeader";
import { SystemFooter } from "./components/SystemFooter";
import {
  commandCenterSnapshot,
  REMEMBERED_USER_ID_KEY,
} from "./data/commandCenterData";
import type { LoginValidationErrors } from "./types/login.types";
import {
  loginRequest,
  resolveLoginError,
  validateLoginCredentials,
} from "../../services/authService";
import { FRONTEND_VERSION } from "../../constants/version";
import styles from "./MesLoginPage.module.css";

/**
 * MES 로그인 페이지 (Command Center + 로그인 패널)
 */
export function MesLoginPage() {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [rememberId, setRememberId] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<LoginValidationErrors>({});
  const [logoText, setLogoText] = useState("MES");

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBERED_USER_ID_KEY);
    if (saved) {
      setUserId(saved);
      setRememberId(true);
    }

    const host = window.location.hostname;
    if (host === "192.168.0.237") {
      setLogoText("MES(237)");
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    setFormError(null);
    const errors = validateLoginCredentials({ userId, password });
    setFieldErrors(errors);
    if (errors.userId || errors.password) {
      return;
    }

    setLoading(true);
    try {
      if (rememberId) {
        localStorage.setItem(REMEMBERED_USER_ID_KEY, userId.trim());
      } else {
        localStorage.removeItem(REMEMBERED_USER_ID_KEY);
      }

      await loginRequest({ userId, password, rememberId });
      window.location.href = "/Terminals";
    } catch (err) {
      const { message } = resolveLoginError(err);
      setFormError(message);
    } finally {
      setLoading(false);
    }
  }, [userId, password, rememberId]);

  return (
    <div className={styles.page}>
      <aside className={styles.visual} aria-label="MES Command Center">
        <CommandCenterVisual snapshot={commandCenterSnapshot} />
      </aside>

      <main className={styles.auth}>
        <div className={styles.mobileHeader}>
          <SystemHeader
            systemStatus={commandCenterSnapshot.systemStatus}
            hideClock
          />
        </div>

        <div className={styles.panelWrap}>
          <LoginPanel
            logoText={logoText}
            userId={userId}
            password={password}
            rememberId={rememberId}
            loading={loading}
            formError={formError}
            fieldErrors={fieldErrors}
            onUserIdChange={(v) => {
              setUserId(v);
              if (fieldErrors.userId) {
                setFieldErrors((prev) => ({ ...prev, userId: undefined }));
              }
            }}
            onPasswordChange={(v) => {
              setPassword(v);
              if (fieldErrors.password) {
                setFieldErrors((prev) => ({ ...prev, password: undefined }));
              }
            }}
            onRememberIdChange={setRememberId}
            onSubmit={handleSubmit}
          />
        </div>

        <div className={styles.mobileFooter}>
          <SystemFooter year={2026} />
        </div>

        <span className={styles.buildVersion} aria-label={`빌드 버전 ${FRONTEND_VERSION}`}>
          {FRONTEND_VERSION}
        </span>
      </main>
    </div>
  );
}

export default MesLoginPage;
